import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const flagSvgs = {
  es: '<rect width="640" height="480" fill="#AA151B"/><rect y="160" width="640" height="160" fill="#F1BF00"/>',
  en: '<path fill="#012169" d="M0 0h640v480H0z"/><path fill="#FFF" d="M75 0l244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l239-178L0 64V0h75z"/><path fill="#C8102E" d="M424 281l216 159v40L369 281h55zm-184 20l6 35L54 480H0l240-179zM640 0v3L391 191l2-44L590 0h50zM0 0l239 176h-60L0 42V0z"/><path fill="#FFF" d="M241 0v480h160V0H241zM0 160v160h640V160H0z"/><path fill="#C8102E" d="M0 193v96h640v-96H0zM273 0v480h96V0h-96z"/>',
  ro: '<rect width="213.33" height="480" x="0" fill="#002B7F"/><rect width="213.33" height="480" x="213.33" fill="#FCD116"/><rect width="213.33" height="480" x="426.67" fill="#CE1126"/>',
  ru: '<rect width="640" height="160" y="0" fill="#FFFFFF"/><rect width="640" height="160" y="160" fill="#0039A6"/><rect width="640" height="160" y="320" fill="#D52B1E"/>'
};

const codes = { es: 'ES', en: 'EN', ro: 'RO', ru: 'RU' };

export default function Navbar() {
  const { currentLang, setLanguage, t } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const langRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';

  const scrollTo = (id) => {
    if (!isHome) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav id="navbar" style={{ zIndex: 100000 }}>
      <div className="nav-container">
        <Link to="/" className="logo">
          <span>{t('brand')}</span>
        </Link>

        <button className="mobile-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
          ☰
        </button>

        <ul className={`nav-links${menuOpen ? ' active' : ''}`} id="navLinks">
          <li>
            <Link to="/pricing" className={location.pathname === '/pricing' ? 'active' : ''}>
              {t('nav_pricing')}
            </Link>
          </li>
          <li>
            {isHome ? (
              <a href="#gallery" onClick={(e) => { e.preventDefault(); scrollTo('gallery'); }}>
                {t('nav_gallery')}
              </a>
            ) : (
              <Link to="/">{t('nav_gallery')}</Link>
            )}
          </li>
          <li>
            {isHome ? (
              <a href="#testimonials" onClick={(e) => { e.preventDefault(); scrollTo('testimonials'); }}>
                {t('nav_reviews')}
              </a>
            ) : (
              <Link to="/">{t('nav_reviews')}</Link>
            )}
          </li>
          <li>
            {isHome ? (
              <a href="#locations" onClick={(e) => { e.preventDefault(); scrollTo('locations'); }}>
                {t('nav_locations')}
              </a>
            ) : (
              <Link to="/">{t('nav_locations')}</Link>
            )}
          </li>
          <li className="lang-nav-item" ref={langRef}>
            <div className={`lang-dropdown${langOpen ? ' open' : ''}`} id="langDropdown">
              <button className="lang-toggle" onClick={() => setLangOpen(!langOpen)}>
                <svg className="flag-icon" viewBox="0 0 640 480" dangerouslySetInnerHTML={{ __html: flagSvgs[currentLang] }} />
                <span className="lang-code">{codes[currentLang]}</span>
                <span className="arrow">▾</span>
              </button>
              <div className="lang-menu" id="langMenu">
                {['es', 'en', 'ro', 'ru'].map((lang) => (
                  <button
                    key={lang}
                    className={`lang-option${currentLang === lang ? ' active' : ''}`}
                    onClick={() => { setLanguage(lang); setLangOpen(false); }}
                    data-lang={lang}
                  >
                    <svg className="flag-icon" viewBox="0 0 640 480" dangerouslySetInnerHTML={{ __html: flagSvgs[lang] }} />
                    <span>{lang === 'es' ? 'Español' : lang === 'en' ? 'English' : lang === 'ro' ? 'Română' : 'Русский'}</span>
                  </button>
                ))}
              </div>
            </div>
          </li>
        </ul>
      </div>

      <style>{`
        nav {
          position: fixed;
          top: 0;
          width: 100%;
          background: rgba(255,255,255,0.97);
          backdrop-filter: blur(10px);
          z-index: 1000;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
        }
        .nav-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 1rem 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .logo {
          font-family: 'Bodoni Moda', 'Didot', 'Bodoni MT', serif;
          font-size: 1.8rem;
          font-weight: 700;
          color: rgb(0, 0, 0);
          text-decoration: none;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }
        .nav-links {
          display: flex;
          gap: 2rem;
          list-style: none;
        }
        .nav-links a {
          text-decoration: none;
          color: var(--primary);
          font-weight: 500;
          transition: color 0.3s;
          position: relative;
          font-size: 0.95rem;
          cursor: pointer;
        }
        .nav-links a::after {
          content: '';
          position: absolute;
          bottom: -5px;
          left: 0;
          width: 0;
          height: 2px;
          background: var(--accent);
          transition: width 0.3s;
        }
        .nav-links a:hover::after,
        .nav-links a.active::after {
          width: 100%;
        }
        .mobile-menu-btn {
          display: none;
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: var(--primary);
        }
        .lang-nav-item {
          position: relative;
          margin-left: 1rem;
        }
        .lang-toggle {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border: none;
          border-radius: 8px;
          background: transparent;
          cursor: pointer;
          font-family: inherit;
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--primary);
          transition: color 0.3s ease;
        }
        .lang-toggle:hover {
          color: var(--accent);
        }
        .flag-icon {
          width: 20px;
          height: 15px;
          border-radius: 2px;
          display: block;
          flex-shrink: 0;
        }
        .arrow {
          font-size: 0.65rem;
          transition: transform 0.3s;
        }
        .lang-dropdown.open .arrow {
          transform: rotate(180deg);
        }
        .lang-menu {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          background: var(--white);
          border-radius: 12px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.15);
          padding: 8px;
          min-width: 170px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all 0.25s ease;
          z-index: 1002;
        }
        .lang-dropdown.open .lang-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }
        .lang-option {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 10px 14px;
          border: none;
          background: transparent;
          border-radius: 8px;
          cursor: pointer;
          font-family: inherit;
          font-size: 0.95rem;
          color: var(--text);
          transition: all 0.3s ease;
          text-align: left;
        }
        .lang-option:hover {
          background: var(--light);
        }
        .lang-option.active {
          background: var(--accent);
          color: var(--white);
        }
        @media (max-width: 768px) {
          .nav-container { flex-wrap: wrap; }
          .nav-links {
            display: none;
            position: absolute;
            top: 100%;
            left: 0;
            width: 100%;
            background: white;
            flex-direction: column;
            padding: 1rem;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            gap: 1rem;
          }
          .nav-links.active { display: flex; }
          .mobile-menu-btn { display: block; }
          .lang-nav-item {
            margin-left: 0;
            margin-top: 0.5rem;
            padding: 0.5rem 0;
            border-top: 1px solid #eee;
          }
          .lang-toggle {
            width: 100%;
            justify-content: center;
            padding: 10px;
          }
          .lang-menu {
            position: static;
            opacity: 1;
            visibility: visible;
            transform: none;
            box-shadow: none;
            display: none;
            margin-top: 0.5rem;
            background: var(--light);
          }
          .lang-dropdown.open .lang-menu { display: block; }
        }
      `}</style>
    </nav>
  );
}
